/**
 * Uma linha de resultado — 34px, sempre.
 *
 * A homogeneidade é o ponto: como TODA linha tem a mesma altura, um realce de
 * foco só basta, e a lista não se move quando o foco anda. Foi essa propriedade
 * que fez a rodada 1 de design escolher duas colunas — o card alto vive no
 * painel direito, nunca aqui.
 */
import {
  Clock,
  FileText,
  LayoutGrid,
  MessageCircle,
  SlidersHorizontal,
  User,
  Users,
} from 'lucide-react';

import { HighlightTerm } from './HighlightTerm';

import type { SpotlightItem, SpotlightSectionId } from './types';
import type { LucideIcon } from 'lucide-react';

/**
 * `User` (silhueta) para pessoa e `Users` (duas silhuetas) para operador — é o
 * par do mockup. A primeira versão usava `Contact`, que desenha um cartão de
 * identificação e lia como «documento» na linha (desvio pego no smoke visual).
 */
const ICON_BY_SECTION: Record<SpotlightSectionId, LucideIcon> = {
  'conversation-id': MessageCircle,
  config: SlidersHorizontal,
  people: User,
  operators: Users,
  screens: LayoutGrid,
  documents: FileText,
  messages: MessageCircle,
  recents: Clock,
};

export function SpotlightRow({
  item,
  focused,
  onOpen,
  term = '',
}: {
  item: SpotlightItem;
  focused: boolean;
  onOpen: () => void;
  /** Termo digitado, para realçar o trecho casado no rótulo. */
  term?: string;
}): JSX.Element {
  const Icon = ICON_BY_SECTION[item.section];
  return (
    <button
      type="button"
      className={`spotlight__row${focused ? ' spotlight__row--focused' : ''}`}
      onClick={onOpen}
      // A navegação é do campo de busca (setas + Enter): a linha não entra na
      // ordem de tabulação, senão Tab sairia do campo em vez de pular seção.
      tabIndex={-1}
      data-testid={`spotlight-row-${item.key}`}
      aria-current={focused ? 'true' : undefined}
    >
      <span className="spotlight__row-icon">
        <Icon size={14} aria-hidden />
      </span>
      <span className="spotlight__row-label">
        <HighlightTerm text={item.label} term={term} />
      </span>
      {item.meta !== undefined && <span className="spotlight__row-meta">{item.meta}</span>}
    </button>
  );
}
